import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, Eye, EyeOff } from "lucide-react";
import { generateLegalDoc, generateLegalDocPdf, today, type DocSection } from "./legalDocUtils";
import { LegalDocPreview } from "./LegalDocPreview";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { LegalField } from "./LegalField";
import { amountWithWords, formatUsd, positiveAmountMessage } from "./amountWords";
import { parseNumber } from "@/tools/converters/numberInput";
import { todayIso } from "./legalClauses";
import { NoticeBox } from "@/components/NoticeBox";
import { parsePaymentDate, paymentDate, formatFullDate } from "@/tools/converters/paymentDates";

const TOOL_INFO_CONTENT = {
  what: `Generates a promissory note \u2014 a written promise to repay a loan \u2014 documenting the terms of a personal or informal loan between two parties.`,
  how: `Fill in the lender, borrower, loan amount, and repayment terms. Preview the note, then download it as a Word or PDF file.`,
  tip: `Putting even an informal loan between friends or family in writing, however casually, meaningfully reduces the odds of a disagreement later about what was actually agreed to.`,
};

/** Level monthly payment for an amortized loan (rate in % per year, 0 = simple division). */
export function monthlyPayment(principal: number, annualRatePct: number, months: number): number {
  if (!(principal > 0) || !(months > 0)) return 0;
  const r = annualRatePct / 100 / 12;
  const pay = r === 0 ? principal / months : (principal * r) / (1 - Math.pow(1 + r, -months));
  return Math.round(pay * 100) / 100;
}

/** Plain-language cautions shown beside the form (not legal advice). */
export function noteWarnings(amount: number, ratePct: number): string[] {
  const out: string[] = [];
  if (ratePct > 10) out.push("Many states cap interest on private loans (often 6–12%, some higher for written agreements). Check your state's usury limit before choosing this rate; charging more can make the note unenforceable or carry penalties.");
  if (amount > 10000 && ratePct < 4) out.push("Loans over $10,000 with little or no interest can trigger IRS imputed-interest or gift-tax rules (the Applicable Federal Rate, AFR, is published monthly). If the lender and borrower are family or related, ask a tax professional.");
  return out;
}

const TITLE = "Promissory Note";

export default function PromissoryNoteGenerator() {
  const [lender, setLender] = useState("");
  const [borrower, setBorrower] = useState("");
  const [amount, setAmount] = useState("");
  const [interestRate, setInterestRate] = useState("0");
  const [repaymentTerms, setRepaymentTerms] = useState("in equal monthly installments over 12 months");
  const [state, setState] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [plan, setPlan] = useState<"installments" | "lump" | "custom">("installments");
  const [months, setMonths] = useState("12");
  const [firstDue, setFirstDue] = useState("");
  const [maturity, setMaturity] = useState("");

  const amountNum = parseNumber(amount);
  const amountMsg = positiveAmountMessage(amount, amountNum, "Loan amount");
  const rateNum = interestRate.trim() === "" ? 0 : parseNumber(interestRate);
  const rateMsg = rateNum === null ? "Interest rate must be a number, for example 5 or 0." : rateNum < 0 || rateNum > 100 ? "Interest rate must be between 0 and 100 per cent per year." : null;
  const n = Number(months);
  const monthsMsg = plan === "installments" && !(Number.isInteger(n) && n >= 1 && n <= 600) ? "Number of months must be a whole number from 1 to 600." : null;
  const firstDueNote = plan === "installments" && firstDue && firstDue < todayIso() ? "The first payment date is in the past. Check that it is right." : null;
  const maturityNote = plan === "lump" && maturity && maturity < todayIso() ? "The maturity date is in the past. Check that it is right." : null;
  const pay = monthlyPayment(amountNum ?? 0, rateNum ?? 0, monthsMsg ? 0 : n);
  const firstDueYmd = plan === "installments" && !monthsMsg ? parsePaymentDate(firstDue) : null;
  const lastDue = firstDueYmd ? paymentDate(firstDueYmd, n - 1) : null;
  const fmtDate = (iso: string) => { const [y, m, d] = iso.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { timeZone: "UTC", year: "numeric", month: "long", day: "numeric" }); };
  const planOk =
    plan === "installments" ? !monthsMsg && !!firstDue && pay > 0 :
    plan === "lump" ? !!maturity :
    !!repaymentTerms.trim();
  const repaymentText =
    plan === "installments" ? `in ${n} equal monthly installments of ${formatUsd(pay)}, the first due on ${firstDue ? fmtDate(firstDue) : "[date]"} and the same day of each month after that, until the full principal and accrued interest are paid` :
    plan === "lump" ? `in one payment of the full principal and accrued interest on ${maturity ? fmtDate(maturity) : "[maturity date]"} (the "Maturity Date")` :
    repaymentTerms.trim();
  const missingRequired = !lender.trim() || !borrower.trim() || !amount.trim() || !!amountMsg || amountNum === null || !!rateMsg || !planOk || !state.trim();
  const warnings = noteWarnings(amountNum ?? 0, rateNum ?? 0);

  const buildSections = (): DocSection[] => [
    {
      paragraphs: [
        `For value received, ${borrower || "[Borrower Name]"} ("Borrower") promises to pay ${lender || "[Lender Name]"} ("Lender") the principal sum of ${amountNum !== null && amountNum > 0 ? amountWithWords(amountNum) : "[Amount]"}, as described below. This Note is made as of ${today()}.`,
      ],
    },
    {
      heading: "1. Interest",
      paragraphs: [
        (rateNum ?? 0) > 0
          ? `Interest shall accrue on the unpaid principal balance at a rate of ${rateNum}% per year.`
          : "This is an interest-free loan.",
      ],
    },
    {
      heading: "2. Repayment",
      paragraphs: [`Borrower agrees to repay this Note ${repaymentText}.`, "Borrower may prepay all or part of the balance at any time without penalty. Payments are applied first to accrued interest, then to principal."],
    },
    {
      heading: "3. Default",
      paragraphs: ["If Borrower fails to make any payment when due and does not cure the default within 15 days of written notice, the entire unpaid balance becomes immediately due and payable at Lender's option."],
    },
    {
      heading: "4. Governing Law",
      paragraphs: [`This Note is governed by the laws of ${state || "[State]"}.`],
    },
    {
      heading: "Signatures",
      paragraphs: ["Borrower: _________________________  Date: _____________", "Lender: _________________________  Date: _____________"],
    },
  ];

  // Was useLogToolUsage(true, 3000, { ownActions: true }) -- a debounced logger with no
  // gating at all, while the real value-delivering actions (the Word/
  // PDF download buttons below) had ZERO usage-limit check of any
  // kind, not even the weak kind -- confirmed directly: clicking
  // either button at the daily limit generated and downloaded a real,
  // complete document. logUsageNow() gates each button's own click
  // directly, same pattern as Merge PDF's own discrete action.
  const { logUsageNow } = useLogUsageOnAction();


  return (
    <ToolContainer>
        <div className="max-w-md mx-auto space-y-4">
          <LegalField label="Lender Name *"><Input value={lender} onChange={e => setLender(e.target.value)} /></LegalField>
          <LegalField label="Borrower Name *"><Input value={borrower} onChange={e => setBorrower(e.target.value)} /></LegalField>
          <LegalField label="Loan Amount ($) *" error={amountMsg} hint={amountNum !== null && !amountMsg ? amountWithWords(amountNum) : undefined}><Input type="text" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="10,000" /></LegalField>
          <LegalField label="Interest Rate (% per year, 0 if none)" error={rateMsg}><Input type="text" inputMode="decimal" value={interestRate} onChange={e => setInterestRate(e.target.value)} /></LegalField>
          <LegalField label="Repayment plan *">
            <select className="w-full border border-border rounded-md bg-background px-2 py-2 text-base sm:text-sm" value={plan} onChange={e => setPlan(e.target.value as typeof plan)}>
              <option value="installments">Equal monthly installments</option>
              <option value="lump">One payment on a maturity date</option>
              <option value="custom">Describe it in my own words</option>
            </select>
          </LegalField>
          {plan === "installments" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LegalField label="Number of months" error={monthsMsg}><Input type="number" inputMode="numeric" min="1" value={months} onChange={e => setMonths(e.target.value)} /></LegalField>
              <LegalField label="First payment due"><Input type="date" value={firstDue} onChange={e => setFirstDue(e.target.value)} /></LegalField>
              {firstDueNote && <NoticeBox className="sm:col-span-2 text-xs" role="status">{firstDueNote}</NoticeBox>}
              {firstDueYmd && lastDue && <p className="sm:col-span-2 text-xs text-muted-foreground" data-testid="promissory-last-due">Last payment due {formatFullDate(lastDue)}.{firstDueYmd.d > 28 ? " The note says the same day of each month. A payment day of the 29th to 31st has no such day in some months, so consider a day from 1 to 28." : ""}</p>}
              {pay > 0 && <p className="sm:col-span-2 text-sm text-muted-foreground">Payment: <strong>{formatUsd(pay)}</strong> per month; total repaid {formatUsd(pay * n)}.</p>}
            </div>
          )}
          {plan === "lump" && <LegalField label="Maturity date" hint={maturityNote ?? undefined}><Input type="date" value={maturity} onChange={e => setMaturity(e.target.value)} /></LegalField>}
          {plan === "custom" && <LegalField label="Repayment terms"><Input value={repaymentTerms} onChange={e => setRepaymentTerms(e.target.value)} /></LegalField>}
          {warnings.map(w => <NoticeBox key={w} className="text-xs">⚠ {w}</NoticeBox>)}
          <LegalField label="State *"><Input value={state} onChange={e => setState(e.target.value)} /></LegalField>
          {missingRequired && (
            <NoticeBox className="text-xs">Fields marked * are required before you can preview or download.</NoticeBox>
          )}

          <Button variant="outline" onClick={() => setShowPreview(v => !v)} disabled={missingRequired} className="w-full gap-2">
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {showPreview ? "Hide Preview" : "Preview"}
          </Button>

          {showPreview && !missingRequired && (
            <LegalDocPreview title={TITLE} sections={buildSections()} />
          )}

          <div className="flex gap-2">
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDoc(TITLE, buildSections(), "promissory-note.docx"); }} disabled={missingRequired} className="flex-1 gap-2">
              <Download className="h-4 w-4" /> Word
            </Button>
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDocPdf(TITLE, buildSections(), "promissory-note.pdf"); }} disabled={missingRequired} variant="secondary" className="flex-1 gap-2">
              <Download className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} disclaimer="This tool generates a general-purpose promissory note template. It isn't legal advice and may not account for the specific laws of your state, country, or situation — requirements for this type of promissory note can vary significantly by jurisdiction. Have any document reviewed by a qualified attorney before you sign it or rely on it, especially for anything involving significant money, property, or legal obligations." />
    </ToolContainer>
  );
}
